/**
 * AdminGeoContent.jsx — GEO İçerik Onay Kuyruğu (liste)
 *
 * Kullanıcının kendi otomasyonu LLM ile makale üretip POST /api/content'e
 * gönderiyor; içerik draft/review durumunda DB'ye düşüyor. Bu ekran admin'in
 * kuyruğu inceleyip detay sayfasına (AdminGeoContentDetail.jsx) geçmesini
 * sağlar. Yayınlama/reddetme/kaldırma AKSİYONLARI burada YOK — bilinçli
 * olarak sadece detay sayfasında (toplu onay = Google "scaled content
 * abuse" riskine karşı ana savunma, bkz. proje-yoneticisi görev talimatı).
 *
 * Backend uçları (require_admin, X-API-Key bu uçlarda kontrol edilmez):
 *   GET /api/admin/geo/articles
 *     query: status (varsayılan "review"), pillar_id, q, sort, page, page_size
 *     yanıt: { items, total, page, page_size, total_pages }
 *     item alanları: article_id, title, slug, pillar_id, subtopic, status,
 *       source_service, created_at, updated_at, published_at, reviewed_by,
 *       reviewed_at — body_md/body_html YOK (detay sayfası çeker)
 *
 * Route: /admin/geo-content
 */
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, AlertTriangle, Eye } from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';
import {
  DataTable,
  FilterBar,
  PageHeader,
  ActionMenu,
  StatusBadge,
  LoadingState,
} from '../components/admin';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getApiErrorMessage } from '../utils/apiError';
import { rootBreadcrumb } from '../utils/adminBreadcrumbs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Durum rozeti eşlemesi — StatusBadge'in mevcut WCAG AA varyantları ────────
const STATUS_VARIANT = {
  draft: 'neutral',
  review: 'warning',
  published: 'success',
  rejected: 'danger',
  archived: 'neutral',
};

// Backend'de pillar_id sabit enum DEĞİL (models/geo.py) — şu an üretimde
// kullanılan iki bilinen değer (bkz. backend/tests/test_geo_admin_articles.py).
// Yeni bir pillar eklenirse listede metin olarak zaten görünür, bu filtre
// sadece bilinen ikisi için kısayol sağlar.
const PILLAR_OPTIONS = [
  { value: 'cabin-crew', labelKey: 'admin.geoContent.pillar.cabinCrew' },
  { value: 'career', labelKey: 'admin.geoContent.pillar.career' },
];

const PAGE_SIZE = 20;
const DEFAULT_STATUS = 'review';

// ── Hata durumu — AdminLegalContent.jsx ErrorState deseninin kopyası ────────
function ErrorState({ msg, onRetry, t }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
      <AlertTriangle size={32} className="text-rose-500" />
      <p className="font-bold text-rose-600">{msg}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 rounded-xl bg-rose-500 text-white text-sm font-bold hover:bg-rose-600 transition-colors min-h-[40px]"
        >
          {t('admin.geoContent.btn.retry')}
        </button>
      )}
    </div>
  );
}

export default function AdminGeoContent() {
  const { t, lang } = useLanguage();
  const { authHeaders, user } = useAuth();
  const tr = (trText, enText) => (lang === 'tr' ? trText : enText);
  const navigate = useNavigate();

  const [status, setStatus] = useState(DEFAULT_STATUS); // null => "all"
  const [pillarId, setPillarId] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Arama debounce (300ms) ──────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        status: status || 'all',
        sort: 'created_at_asc',
        page,
        page_size: PAGE_SIZE,
      };
      if (pillarId) params.pillar_id = pillarId;
      if (debouncedSearch) params.q = debouncedSearch;

      const res = await axios.get(`${API}/admin/geo/articles`, {
        headers: authHeaders(),
        params,
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, lang, status, pillarId, debouncedSearch, page]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // İncelenecek makale yok kuyruğu bu mu — yoksa filtrelenmiş sonuç mu boş —
  // ayrımı için: varsayılan görünüm (review + filtresiz) ile diğer filtreler
  // farklı boş-durum mesajları gösteriyor (bkz. görev talimatı "Boş kuyruk").
  const isDefaultView = status === DEFAULT_STATUS && !pillarId && !debouncedSearch;

  const columns = [
    {
      key: 'title',
      label: t('admin.geoContent.col.title'),
      cellClassName: 'max-w-sm',
      render: (_val, row) => (
        <Link
          to={`/admin/geo-content/${row.article_id}`}
          className="font-semibold text-[var(--text-main)] hover:text-[var(--brand-primary)] transition-colors truncate block"
          title={row.title}
        >
          {row.title}
        </Link>
      ),
    },
    {
      key: 'slug',
      label: t('admin.geoContent.col.slug'),
      render: (_val, row) => (
        <span className="text-xs text-[var(--text-muted)] font-mono">{row.slug}</span>
      ),
    },
    {
      key: 'pillar_id',
      label: t('admin.geoContent.col.pillar'),
      render: (_val, row) => (
        <span className="text-xs text-[var(--text-main)]">{row.pillar_id || '—'}</span>
      ),
    },
    {
      key: 'status',
      label: t('admin.geoContent.col.status'),
      render: (_val, row) => (
        <StatusBadge
          variant={STATUS_VARIANT[row.status] || 'neutral'}
          label={t(`admin.geoContent.status.${row.status}`) || row.status}
          dot={row.status === 'review'}
        />
      ),
    },
    {
      key: 'created_at',
      label: t('admin.geoContent.col.createdAt'),
      render: (_val, row) => (
        <span className="text-xs text-[var(--text-muted)]">
          {row.created_at ? new Date(row.created_at).toLocaleString('tr-TR') : '—'}
        </span>
      ),
    },
    {
      key: 'updated_at',
      label: t('admin.geoContent.col.updatedAt'),
      render: (_val, row) => (
        <span className="text-xs text-[var(--text-muted)]">
          {row.status !== 'draft' && row.updated_at
            ? new Date(row.updated_at).toLocaleString('tr-TR')
            : '—'}
        </span>
      ),
    },
    {
      key: 'source_service',
      label: t('admin.geoContent.col.source'),
      render: (_val, row) => (
        <span className="text-xs text-[var(--text-muted)]">{row.source_service || '—'}</span>
      ),
    },
    {
      key: '_actions',
      label: '',
      render: (_val, row) => (
        <ActionMenu
          items={[
            {
              label: t('admin.geoContent.btn.viewDetail'),
              icon: Eye,
              onClick: () => navigate(`/admin/geo-content/${row.article_id}`),
            },
          ]}
        />
      ),
    },
  ];

  const filters = [
    {
      key: 'status',
      label: t('admin.geoContent.col.status'),
      options: [
        { value: 'draft', label: t('admin.geoContent.status.draft') },
        { value: 'review', label: t('admin.geoContent.status.review') },
        { value: 'published', label: t('admin.geoContent.status.published') },
        { value: 'rejected', label: t('admin.geoContent.status.rejected') },
        { value: 'archived', label: t('admin.geoContent.status.archived') },
      ],
    },
    {
      key: 'pillar_id',
      label: t('admin.geoContent.col.pillar'),
      options: PILLAR_OPTIONS.map((p) => ({ value: p.value, label: t(p.labelKey) })),
    },
  ];

  const activeFilters = { status, pillar_id: pillarId };

  const handleFilterChange = (key, value) => {
    if (key === 'status') {
      setStatus(value);
    } else if (key === 'pillar_id') {
      setPillarId(value);
    }
    setPage(1);
  };

  const handleClearAll = () => {
    setStatus(null);
    setPillarId(null);
    setSearchInput('');
    setDebouncedSearch('');
    setPage(1);
  };

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          rootBreadcrumb(user?.role, tr),
          { label: t('admin.nav.geoContent') },
        ]}
        title={t('admin.geoContent.pageTitle')}
        subtitle={t('admin.geoContent.pageSubtitle')}
      />

      <FilterBar
        search={searchInput}
        onSearchChange={setSearchInput}
        filters={filters}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAll}
        className="mb-4"
      />

      {loading ? (
        <LoadingState variant="table" rows={5} cols={6} />
      ) : error ? (
        <ErrorState msg={error} onRetry={fetchArticles} t={t} />
      ) : (
        <DataTable
          columns={columns}
          data={items}
          rowKey="article_id"
          pageSize={PAGE_SIZE}
          serverPagination
          currentPage={page}
          totalItems={total}
          onPageChange={setPage}
          emptyIcon={isDefaultView ? <Sparkles size={40} strokeWidth={1.5} /> : undefined}
          emptyTitle={isDefaultView ? t('admin.geoContent.empty.title') : t('admin.geoContent.emptyFiltered.title')}
          emptyDescription={
            isDefaultView ? t('admin.geoContent.empty.desc') : t('admin.geoContent.emptyFiltered.desc')
          }
        />
      )}
    </AdminLayout>
  );
}
